import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { GridLogicOperator } from '@mui/x-data-grid';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { ItemEntity, ItemTemplateEntityWithSchema } from '@/types/api';

import { getItemsQueryKey } from '../../api/get-items';
import { updateItem } from '../../api/update-item';
import { ItemFilterValuesProvider } from '../../providers';
import { SelectItemModal } from '../select-item-modal';

import { AttributeBulkEditForm } from './attribute-bulk-edit-form';
import { BulkEditProgress, type BulkEditProgressRow } from './bulk-edit-progress';

type Step = 'select' | 'attributes' | 'progress';

export interface BulkEditDialogProps {
  open: boolean;
  onClose: () => void;
  mainItem: ItemEntity;
  itemTemplate: ItemTemplateEntityWithSchema;
}

interface SelectionForm {
  selectedItems: ItemEntity[];
}

export const BulkEditDialog = ({ open, onClose, mainItem, itemTemplate }: BulkEditDialogProps) => {
  const { t } = useTranslation();
  const { orgId, projectId } = useScopeIds('org', 'project');
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('select');
  const [selectedItems, setSelectedItems] = useState<ItemEntity[]>([]);
  const [progressRows, setProgressRows] = useState<BulkEditProgressRow[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastSelectedAttributes, setLastSelectedAttributes] = useState<Record<string, unknown>>({});

  // RHF context for SelectItemModal (it requires useFormContext)
  const selectForm = useForm<SelectionForm>({
    defaultValues: { selectedItems: [] },
  });

  const resetAndClose = useCallback(() => {
    setStep('select');
    setSelectedItems([]);
    setProgressRows([]);
    setIsExecuting(false);
    setLastSelectedAttributes({});
    selectForm.reset({ selectedItems: [] });
    onClose();
  }, [selectForm, onClose]);

  const handleItemSelection = useCallback(() => {
    const items = selectForm.getValues('selectedItems');
    const selected = Array.isArray(items) ? items : [];
    if (selected.length > 0) {
      setSelectedItems(selected);
      setStep('attributes');
    }
  }, [selectForm]);

  const handleItemSelectionCancel = useCallback(() => {
    resetAndClose();
  }, [resetAndClose]);

  const handleAttributeCancel = () => {
    setStep('select');
  };

  const runUpdate = useCallback(
    async (targets: ItemEntity[], selectedAttributes: Record<string, unknown>) => {
      setProgressRows(
        targets.map((item) => ({
          id: item.id,
          name: item.name,
          itemNumber: item.itemNumber,
          status: 'pending' as const,
        })),
      );
      setStep('progress');
      setIsExecuting(true);

      const updateRow = (id: string, patch: Partial<BulkEditProgressRow>) => {
        setProgressRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
      };

      for (let i = 0; i < targets.length; i += 5) {
        await Promise.all(
          targets.slice(i, i + 5).map(async (target) => {
            updateRow(target.id, { status: 'in-progress' });
            try {
              await updateItem(orgId, projectId, target.id, { attributes: selectedAttributes });
              updateRow(target.id, { status: 'success' });
            } catch (err) {
              updateRow(target.id, {
                status: 'fail',
                error: err instanceof Error ? err.message : String(err),
              });
            }
          }),
        );
      }

      setIsExecuting(false);
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
    },
    [orgId, projectId, queryClient],
  );

  const handleExecute = useCallback(
    async (selectedAttributes: Record<string, unknown>) => {
      setLastSelectedAttributes(selectedAttributes);
      const targets: ItemEntity[] = [mainItem, ...selectedItems];
      await runUpdate(targets, selectedAttributes);
    },
    [mainItem, selectedItems, runUpdate],
  );

  const handleRetryItem = useCallback(
    async (itemId: string) => {
      const allTargets = [mainItem, ...selectedItems];
      const target = allTargets.find((item) => item.id === itemId);
      if (!target) return;

      setProgressRows((prev) =>
        prev.map((row) =>
          row.id === itemId ? { ...row, status: 'in-progress', error: undefined } : row,
        ),
      );

      setIsExecuting(true);
      try {
        await updateItem(orgId, projectId, itemId, { attributes: lastSelectedAttributes });
        setProgressRows((prev) =>
          prev.map((row) => (row.id === itemId ? { ...row, status: 'success' } : row)),
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setProgressRows((prev) =>
          prev.map((row) =>
            row.id === itemId ? { ...row, status: 'fail', error: errorMessage } : row,
          ),
        );
      }
      setIsExecuting(false);

      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });
    },
    [mainItem, selectedItems, orgId, projectId, lastSelectedAttributes, queryClient],
  );

  const attributesDialogTitle = t('items.bulkEdit.attributesTitle');
  const progressDialogTitle = t('items.bulkEdit.progress.title');

  return (
    <>
      {/* Step 1: Select items — SelectItemModal renders its own Dialog */}
      {open && step === 'select' && (
        <ItemFilterValuesProvider
          filterValues={{
            filterModel: [
              {
                items: [
                  {
                    field: 'itemTemplateId',
                    operator: 'equals',
                    value: mainItem.itemTemplateId,
                  },
                ],
                logicOperator: GridLogicOperator.And,
              },
            ],
            excludeIds: [mainItem.id],
          }}
        >
          <FormProvider {...selectForm}>
            {/*
             * SelectItemModal calls onClose after both "Select" (after setting form value)
             * and "Cancel" (after resetting form value). We detect which path by checking
             * whether the form now has items selected.
             */}
            <SelectItemModal
              name="selectedItems"
              open={true}
              onClose={() => void 0}
              onSelect={handleItemSelection}
              onCancel={handleItemSelectionCancel}
              multiValue={true}
              showStatusColumn={true}
              isRowSelectable={(row) => !row.locked}
              title={t('items.bulkEdit.selectTitle')}
            />
          </FormProvider>
        </ItemFilterValuesProvider>
      )}

      {/* Step 2: Attribute selection */}
      {open && step === 'attributes' && (
        <Dialog open={true} onClose={handleAttributeCancel} maxWidth="md" fullWidth>
          <DialogTitle>{attributesDialogTitle}</DialogTitle>
          <DialogContent>
            <AttributeBulkEditForm
              template={itemTemplate}
              mainInstanceAttributes={mainItem.attributes}
              onExecute={(attrs) => {
                void handleExecute(attrs);
              }}
              onCancel={handleAttributeCancel}
              isSubmitting={isExecuting}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Step 3: Progress */}
      {open && step === 'progress' && (
        <Dialog open={true} maxWidth="md" fullWidth>
          <DialogTitle>{progressDialogTitle}</DialogTitle>
          <DialogContent>
            <BulkEditProgress
              rows={progressRows}
              onRetry={(id) => {
                void handleRetryItem(id);
              }}
              onClose={resetAndClose}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
